import React, { useEffect } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Sidebar } from '@/components/dashboard/Sidebar';
import { NotificationBell } from '@/components/dashboard/NotificationBell';
import { UserMenu } from '@/components/dashboard/UserMenu';
import { Loader2 } from 'lucide-react';
import { ConnectionStatus } from '@/components/ConnectionStatus';

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  // Redirecionar admin para /dashboard/admin se estiver em /dashboard
  useEffect(() => {
    if (!loading && user && user.role === 'admin' && location.pathname === '/dashboard') {
      navigate('/dashboard/admin', { replace: true });
    }
  }, [user, loading, location.pathname, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-green-500 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="flex h-screen bg-background hero-gradient overflow-hidden font-sans">
      <Sidebar />
      <main className="flex-1 overflow-y-auto relative flex flex-col">
        {/* Top Header */}
        <header className="h-14 border-b border-gray-100 bg-white/80 backdrop-blur-md sticky top-0 z-40 flex items-center justify-end px-4 sm:px-6 lg:px-8 gap-3">
          {user.subscription_plan === 'free' && (
            <Link
              to="/dashboard/assinatura"
              className="relative inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg shadow-orange-500/50 transform hover:scale-105 active:scale-95 overflow-hidden ring-1 ring-white/25 animate-pulse-glow"
            >
              <span className="absolute inset-0 bg-white/10" />
              <span className="absolute inset-0 pointer-events-none">
                <span className="absolute inset-y-0 -left-1/2 w-1/2 bg-gradient-to-r from-transparent via-white/45 to-transparent blur-sm animate-premium-shimmer" />
              </span>
              <span className="relative z-10 inline-flex items-center gap-2">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                SEJA PREMIUM
              </span>
            </Link>
          )}

          <NotificationBell />
          <UserMenu />
        </header>

        <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto w-full">
          <Outlet context={{ user }} />
        </div>
      </main>
      <ConnectionStatus />
    </div>
  );
}
